import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import CustomButton from "components/Screens/Marketplace/Customer/common/Button";
import PlayButton from "components/Screens/Marketplace/Customer/common/PlayButton";
import VideoModal from "components/Screens/Marketplace/Customer/common/VideoModal";
import ProfileTabs from "components/Screens/Marketplace/Customer/common/ProfileTabs";
import ProfileContent, {
  getAvailableTabs,
} from "components/Screens/Marketplace/Customer/ProfileSection/ProfileContent";
import InterviewSuccessModal from "components/Screens/Marketplace/Customer/TalentNetwork/InterviewSuccessModal";
import fetchTalentProfile, { requestInterview, checkInterviewRequested } from "services/network/talent";
import ShortlistButton from "components/Screens/Marketplace/Customer/ProfileSection/ShortlistButton";
import useProfileViewTracking from "components/Screens/Marketplace/Customer/ProfileSection/useProfileViewTracking";
import { analytics } from "services/analytics";
import type { TalentProfile } from "components/Screens/Marketplace/Customer/types";
import getTalentFullName from "components/Screens/Marketplace/Customer/utils/talentName";

import layer1 from "components/Screens/Marketplace/Customer/assets/video-layers/layer1.svg";
import layer2Left from "components/Screens/Marketplace/Customer/assets/video-layers/layer2-left.svg";
import layer2Right from "components/Screens/Marketplace/Customer/assets/video-layers/layer2-right.svg";
import layer4Left from "components/Screens/Marketplace/Customer/assets/video-layers/layer4-left.svg";
import layer4Right from "components/Screens/Marketplace/Customer/assets/video-layers/layer4-right.svg";
import layer2LeftMobile from "components/Screens/Marketplace/Customer/assets/video-layers/layer2-left-mobile.svg";
import layer2RightMobile from "components/Screens/Marketplace/Customer/assets/video-layers/layer2-right-mobile.svg";
import coverImage from "components/Screens/Marketplace/Customer/assets/common/cover.jpg";

import "components/Screens/Marketplace/Customer/ProfileSection/ProfileSection.scss";
import { baseUrl } from "util/APIBaseUrl";
import { getIdToken } from "util/APIHelper";

// Provide a safe fallback in non-webpack environments (e.g., Jest)
import PakistanFlag from "assets/svgs/PakistanFlag";
import CostaRicaFlag from "assets/svgs/CostaRicaFlag";
import PeruFlag from "assets/svgs/PeruFlag";

const FLAG_COMPONENTS: Record<string, React.ComponentType<{ width?: string; height?: string }>> = {
  Pakistan: PakistanFlag,
  "Costa Rica": CostaRicaFlag,
  Peru: PeruFlag,
};

interface ProfileSectionProps {
  className?: string;
  profileId?: string;
  initialTalent: TalentProfile; // now required
}

function ProfileSection({ className = "", profileId, initialTalent }: ProfileSectionProps) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isStickyActive, setIsSticky] = useState(false);
  const [interviewRequested, setInterviewRequested] = useState(false);
  const [isRequestingInterview, setIsRequestingInterview] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const stickyRef = useRef<HTMLDivElement | null>(null);
  const namePronunciationAudioRef = useRef<HTMLAudioElement | null>(null);
  const [namePronunciationUrl, setNamePronunciationUrl] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();

  // eslint-disable-next-line no-underscore-dangle
  const talentId = initialTalent?._id;
  const talentName = initialTalent ? `${initialTalent.firstName} ${initialTalent.lastName}`.trim() : "";

  useEffect(() => {
    if (!talentId) return;
    const linkToken = new URLSearchParams(location.search).get("token") || undefined;
    analytics.track("Talent Profile Viewed", {
      talentId,
      talentName,
      ...(linkToken ? { linkToken } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId]);

  // eslint-disable-next-line no-underscore-dangle
  useProfileViewTracking(initialTalent?._id);

  const availableTabs = useMemo(() => getAvailableTabs(initialTalent), [initialTalent]);

  const FlagComponent = initialTalent?.country ? FLAG_COMPONENTS[initialTalent.country] : undefined;

  const loggedInUser = useMemo(() => {
    try {
      const stored = localStorage.getItem("loggedInUser");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // Check if interview already requested for this talent
  useEffect(() => {
    const checkRequested = async () => {
      // eslint-disable-next-line no-underscore-dangle
      if (!loggedInUser?.customerId || !initialTalent?._id) return;
      const requested = await checkInterviewRequested(
        loggedInUser.customerId,
        // eslint-disable-next-line no-underscore-dangle
        initialTalent._id
      );
      setInterviewRequested(requested);
    };
    checkRequested();
    // eslint-disable-next-line react-hooks/exhaustive-deps, no-underscore-dangle
  }, [initialTalent?._id]);

  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return undefined;

    // Use IntersectionObserver with a sentinel element positioned right above sticky element
    const sentinel = document.createElement("div");
    sentinel.style.position = "absolute";
    sentinel.style.left = "0";
    sentinel.style.width = "1px";
    sentinel.style.height = "1px";
    sentinel.style.top = "-1px"; // Just above the sticky element
    sentinel.style.pointerEvents = "none";
    sentinel.setAttribute("data-sticky-sentinel", "true");

    el.appendChild(sentinel);

    const observer = new IntersectionObserver(
      () => {
        setIsSticky((prev) => {
          return !prev;
        });
      },
      {
        threshold: [0, 1],
        // rootMargin negative value = offset from viewport top
        rootMargin: isMobile ? "0px" : "-56px 0px 0px 0px",
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      if (sentinel.parentNode) {
        sentinel.parentNode.removeChild(sentinel);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlayVideo = () => {
    if (initialTalent?.introVideoUrl) {
      setIsVideoModalOpen(true);
      analytics.track("Talent Video Opened", { talentId, talentName });
    }
  };

  const handleCloseVideo = () => {
    setIsVideoModalOpen(false);
    analytics.track("Talent Video Playback Exited", { talentId, talentName });
  };

  // Signed URLs are time-limited; if a stale one fails to load, refetch the
  // profile once to get a fresh URL and retry playback.
  const refetchPronunciationUrl = async (): Promise<string | undefined> => {
    if (!talentId) return undefined;
    try {
      const fresh = await fetchTalentProfile(talentId);
      const url = fresh?.namePronunciationUrl;
      if (url) setNamePronunciationUrl(url);
      return url;
    } catch {
      return undefined;
    }
  };

  const playPronunciation = async (url: string, allowRetry: boolean) => {
    if (!namePronunciationAudioRef.current) {
      namePronunciationAudioRef.current = new Audio();
    }
    const audio = namePronunciationAudioRef.current;
    audio.src = url;
    audio.currentTime = 0;
    try {
      await audio.play();
    } catch {
      if (allowRetry) {
        const fresh = await refetchPronunciationUrl();
        if (fresh) await playPronunciation(fresh, false);
      }
    }
  };

  const handlePlayPronunciation = () => {
    const url = namePronunciationUrl || initialTalent?.namePronunciationUrl;
    if (!url) return;
    analytics.track("Talent Name Pronunciation Played", { talentId, talentName });
    playPronunciation(url, true).catch(() => {});
  };

  const handleRequestInterview = useCallback(async () => {
    if (!loggedInUser || !initialTalent || interviewRequested || isRequestingInterview) return;
    setIsRequestingInterview(true);
    try {
      await requestInterview({
        customerId: loggedInUser.customerId,
        customerUserName: loggedInUser.name || "",
        customerUserEmail: loggedInUser.email || "",
        customerUserId: loggedInUser.userId || "",
        // eslint-disable-next-line no-underscore-dangle
        talentId: initialTalent._id,
        talentName: getTalentFullName(initialTalent),
        // eslint-disable-next-line no-underscore-dangle
        talentProfileUrl: `${window.location.origin}/main/talent-network/${initialTalent._id}`,
        requestedAt: new Date().toLocaleString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      });
      setInterviewRequested(true);
      analytics.track("Talent Interview Requested", { talentId, talentName });
      setShowInterviewModal(true);
    } catch {
      // silently fail
    } finally {
      setIsRequestingInterview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, initialTalent, interviewRequested, isRequestingInterview]);

  const handleDownloadPDF = async () => {
    if (!profileId) {
      return;
    }

    try {
      const token = getIdToken();
      // eslint-disable-next-line no-underscore-dangle
      const response = await fetch(`${baseUrl}/talents/${initialTalent?._id}/profile/portal?pdf=true`, {
        headers: {
          Authorization: `Bearer ${token.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();

      const contentDisposition = response.headers.get("content-disposition");
      let filename = "resumeFile.pdf";

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          // eslint-disable-next-line prefer-destructuring
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      analytics.track("Talent Profile PDF Downloaded", { talentId, talentName });
    } catch (error) {
      // swallow
    }
  };

  const hideBadges = isMobile && isStickyActive;

  return (
    <>
      <div className={`profile-section ${className}`}>
        <div className="profile-section__timeline">
          <div className="profile-section__timeline-background">
            {initialTalent?.introVideoUrl ? (
              <>
                <img
                  src={layer1}
                  alt="Layer 1 Background"
                  className="profile-section__layer profile-section__layer--1-background"
                />

                <video
                  className="profile-section__video-thumbnail"
                  src={initialTalent?.introVideoUrl}
                  muted
                  playsInline
                  preload="metadata"
                />

                <img
                  src={isMobile ? layer2LeftMobile : layer2Left}
                  alt="Layer 2 Left Gradient"
                  className="profile-section__layer profile-section__layer--2-left"
                />
                <img
                  src={isMobile ? layer2RightMobile : layer2Right}
                  alt="Layer 2 Right Gradient"
                  className="profile-section__layer profile-section__layer--2-right"
                />

                <img
                  src={layer4Left}
                  alt="Layer 4 Left Pattern"
                  className="profile-section__layer profile-section__layer--4-left"
                />
                <img
                  src={layer4Right}
                  alt="Layer 4 Right Pattern"
                  className="profile-section__layer profile-section__layer--4-right"
                />
              </>
            ) : (
              <img
                src={coverImage}
                alt="Layer 1 Background"
                className="profile-section__layer profile-section__layer--1-background"
              />
            )}

            {initialTalent?.introVideoUrl && (
              <div className="profile-section__meet-button-container" onClick={handlePlayVideo}>
                <div className="profile-section__meet-play-icon" aria-label="Play video">
                  <PlayButton />
                </div>
                <div className="profile-section__meet-label">
                  <span>Meet {initialTalent?.firstName || ""}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          ref={stickyRef}
          className={`profile-section__sticky ${!isStickyActive ? "profile-section__sticky--active" : ""}`}
        >
          <div className="profile-section__info">
            {/* User Image overlapping timeline */}
            <div className="profile-section__user-image">
              <img
                src={initialTalent?.profilePicture || ""}
                alt={getTalentFullName(initialTalent)}
                className="profile-section__avatar"
              />
            </div>

            {/* User Details */}
            <div className="profile-section__details">
              <div className="profile-section__name-section">
                <h1 className="profile-section__name">
                  {getTalentFullName(initialTalent)}
                  {initialTalent?.namePronunciationUrl && (
                    <Tooltip title="Hear name pronunciation" placement="top" arrow>
                      <button
                        type="button"
                        className="profile-section__pronunciation-btn"
                        onClick={handlePlayPronunciation}
                        aria-label="Play name pronunciation"
                      >
                        <VolumeUpOutlinedIcon sx={{ fontSize: 18 }} />
                      </button>
                    </Tooltip>
                  )}
                  {FlagComponent && (
                    <span className="profile-section__country-flag">
                      <FlagComponent width="16" height="16" />
                    </span>
                  )}
                </h1>
                <p className="profile-section__experience">
                  {initialTalent?.overview?.totalYearsOfExperience || 0} years of experience
                </p>
              </div>

              {!isMobile && (
                <div className="profile-section__actions">
                  <CustomButton
                    label="Download PDF"
                    color="primary"
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={handleDownloadPDF}
                  />
                  {interviewRequested ? (
                    <CustomButton
                      label="Interview Requested"
                      color="secondary"
                      variant="contained"
                      size="small"
                      startIcon={<CheckIcon />}
                      isDisabled
                      onClick={() => {}}
                    />
                  ) : (
                    <CustomButton
                      label="Request Interview"
                      color="primary"
                      variant="contained"
                      size="small"
                      startIcon={
                        isRequestingInterview ? (
                          <CircularProgress size={16} sx={{ color: "#fff" }} />
                        ) : (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                            <circle cx="12" cy="9.5" r="3" stroke="currentColor" strokeWidth="1.5" />
                            <path
                              d="M6.5 19.5C7 16.5 9.2 14.5 12 14.5C14.8 14.5 17 16.5 17.5 19.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        )
                      }
                      isDisabled={isRequestingInterview}
                      onClick={handleRequestInterview}
                    />
                  )}
                  {/* eslint-disable-next-line no-underscore-dangle */}
                  <ShortlistButton talentId={initialTalent?._id} />
                </div>
              )}
            </div>

            {/* Badges - placeholder for future use */}
            <div
              className="profile-section__badges"
              style={{ display: hideBadges ? "none" : undefined }}
              aria-hidden={hideBadges}
            />

            {isMobile && (
              <div className="profile-section__actions profile-section__actions--mobile">
                <CustomButton
                  label="Download PDF"
                  color="primary"
                  isFullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={handleDownloadPDF}
                />
                {interviewRequested ? (
                  <CustomButton
                    label="Interview Requested"
                    color="secondary"
                    isFullWidth
                    variant="contained"
                    size="small"
                    startIcon={<CheckIcon />}
                    isDisabled
                    onClick={() => {}}
                  />
                ) : (
                  <CustomButton
                    label="Request Interview"
                    color="primary"
                    isFullWidth
                    variant="contained"
                    size="small"
                    startIcon={
                      isRequestingInterview ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                          <circle cx="12" cy="9.5" r="3" stroke="currentColor" strokeWidth="1.5" />
                          <path
                            d="M6.5 19.5C7 16.5 9.2 14.5 12 14.5C14.8 14.5 17 16.5 17.5 19.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      )
                    }
                    isDisabled={isRequestingInterview}
                    onClick={handleRequestInterview}
                  />
                )}
                {/* eslint-disable-next-line no-underscore-dangle */}
                <ShortlistButton talentId={initialTalent?._id} />
              </div>
            )}
          </div>

          {/* Tabs Section */}
          <div className="profile-section__tabs">
            <ProfileTabs tabs={availableTabs} />
          </div>
        </div>

        {/* Content Sections */}
        {initialTalent && <ProfileContent talent={initialTalent} />}
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={handleCloseVideo}
        videoUrl={initialTalent?.introVideoUrl || ""}
        talentId={talentId}
        talentName={talentName}
      />

      {/* Interview Success Modal */}
      <InterviewSuccessModal
        isOpen={showInterviewModal}
        onClose={() => setShowInterviewModal(false)}
        showStayHere
        onViewOtherProfiles={() => {
          setShowInterviewModal(false);
          navigate("/main/talent-network");
        }}
      />
    </>
  );
}

export default ProfileSection;
